/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Injectable } from '@angular/core';
import { ResourceInterface } from '@app/shared/models/resource.interface';
import { HttpClient } from '@angular/common/http';
import { ApiQuery } from '@app/core/http/api-query';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { map } from 'rxjs/operators';
import { HttpResponse, Logger } from '@app/core';
import { HotelBooking } from '@app/hotels/hotel-bookings/hotel-booking.model';
import { Model } from '@app/shared/models/model.model';
import { Room } from '@app/hotels/rooms/room.model';
import { Night } from '@app/hotels/rooms/night.model';
import { SortDescriptor } from '@progress/kendo-data-query';

const log = new Logger('HotelBookingsService');

@Injectable({
  providedIn: 'root',
})
export class HotelBookingsService implements ResourceInterface {
  constructor(private http: HttpClient) {}

  index(query: ApiQuery = new ApiQuery()): Observable<PaginatedResponse> {
    return this.http.get('v1/hotel_bookings', query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        response.data = response.data.map((hotelBooking: HotelBooking) => {
          return new HotelBooking().deserialize(hotelBooking);
        });
        return new PaginatedResponse().deserialize(response);
      })
    );
  }

  update(hotelBooking: HotelBooking): Observable<HotelBooking> {
    return this.http.put('v1/hotel_bookings/' + hotelBooking.id, hotelBooking).pipe(
      map((response: HttpResponse) => {
        return new HotelBooking().deserialize(response.data);
      })
    );
  }

  store(hotelBooking: HotelBooking): Observable<HotelBooking> {
    return this.http.post('v1/hotel_bookings', hotelBooking).pipe(
      map((response: HttpResponse) => {
        return new HotelBooking().deserialize(response.data);
      })
    );
  }

  show(id: number, query: ApiQuery = new ApiQuery()): Observable<Model> {
    return this.http.get('v1/hotel_bookings/' + id, query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        return new HotelBooking().deserialize(response.data);
      })
    );
  }

  destroy(id: number): Observable<any> {
    return this.http.delete('v1/hotel_bookings/' + id);
  }

  indexRooms(query: ApiQuery = new ApiQuery()): Observable<PaginatedResponse> {
    /**
     * Ако сортира по booking_supplier_id го заменя с booking.supplier.name .
     *
     * Прави се, за да може да се използват функции като оператор isNull(за филъра)
     * и едновременно сортирането да става по booking.supplier.name вместо booking_supplier_id.
     * Защото сортирането по ID не е адекватен вариант
     */
    const dataSet = query.getDataSet();
    if (dataSet && dataSet.sort) {
      dataSet.sort.forEach((field: SortDescriptor) => {
        if (field.field === 'booking.supplier_id') {
          field.field = 'booking.supplier.name';
        }
      });
    }

    return this.http.get('v1/hotel_booking_allotments', query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        response.data = response.data.map((room: Room) => {
          return new Room().deserialize(room);
        });
        return new PaginatedResponse().deserialize(response);
      })
    );
  }

  showRooms(allotment_id: number, query: ApiQuery = new ApiQuery()): Observable<any> {
    return this.http.get('v1/hotel_booking_allotments/' + allotment_id, query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        return response.data;
      })
    );
  }

  previewSoldRooms(id: number, query: ApiQuery = new ApiQuery()): Observable<any> {
    return this.http.get('v1/hotel_bookings/' + id + '/preview_sold_rooms', query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        return response.data;
      })
    );
  }

  exportSoldRooms(id: number): Observable<any> {
    return this.http.download('v1/hotel_bookings/' + id + '/export_sold_rooms');
  }

  massReassignAgents(fields: any): Observable<any> {
    return this.http.post('v1/hotel_bookings/mass_reassign_agents', fields).pipe(
      map((response: HttpResponse) => {
        return response.data;
      })
    );
  }

  indexNights(query: ApiQuery = new ApiQuery()): Observable<PaginatedResponse> {
    return this.http.get('v1/hotel_booking_nights', query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        response.data = response.data.map((night: Night) => {
          return new Night().deserialize(night);
        });
        return new PaginatedResponse().deserialize(response);
      })
    );
  }

  totals(booking_id: number, query: ApiQuery = new ApiQuery()): Observable<any> {
    return this.http.get('v1/hotel_booking_allotments/total_amount/' + booking_id, query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        return response.data;
      })
    );
  }
}
